// src/store/api/baseApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { logout, tokenRefreshed } from "../../store/authSlice";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || "https://backend.nessefy.com/api",
  prepareHeaders: (headers) => {
    const token = localStorage.getItem("authToken");
    if (token) headers.set("authorization", `Bearer ${token}`);
    return headers;
  },
});

// Shared in-flight refresh. Concurrent 401s await this single promise
// instead of failing, so every request retries once the token is renewed.
let refreshPromise = null;

// Performs the token refresh exactly once. Resolves to `true` on success
// (new tokens stored), `false` on failure (user logged out).
const refreshTokens = async (api, extraOptions) => {
  const accessToken = localStorage.getItem("authToken");
  const refreshToken = localStorage.getItem("refreshToken");

  if (!accessToken || !refreshToken) {
    api.dispatch(logout());
    return false;
  }

  // Call refresh endpoint directly (not via RTK Query to avoid circular deps)
  const refreshResult = await baseQuery(
    {
      url: "/Auth/refresh-token",
      method: "POST",
      body: { accessToken, refreshToken },
    },
    api,
    extraOptions,
  );

  if (refreshResult?.data?.accessToken) {
    // Store new tokens and update Redux
    api.dispatch(
      tokenRefreshed({
        token: refreshResult.data.accessToken,
        refreshToken: refreshResult.data.refreshToken,
      }),
    );
    return true;
  }

  // Refresh failed — force logout
  api.dispatch(logout());
  return false;
};

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result?.error?.status === 401) {
    // Start a refresh if none is in flight; otherwise reuse the existing one.
    // The `.finally` clears the lock so a later expiry triggers a fresh refresh.
    if (!refreshPromise) {
      refreshPromise = refreshTokens(api, extraOptions).finally(() => {
        refreshPromise = null;
      });
    }

    const refreshed = await refreshPromise;

    if (refreshed) {
      // Retry the original request with the new token. This goes through the
      // raw baseQuery (not baseQueryWithReauth), so it can't recurse.
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Post",
    "Profile",
    "Auth",
    "Messages",
    "Conversations",
    "Chats",
    "Requests",
    "SentRequests",
    "AcceptedRequests",
    "PostNotifications",
    "Notifications",
    "Follow",
  ],
  endpoints: () => ({}),
});
